import { LoginSchema } from '@pm/schemas';
import { Button, Field, Input } from '@pm/ui';
import { Lock, Mail } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ApiRequestError } from '@/lib/api';
import { BrandLogo, BrandMonogram } from '@/components/BrandMark';
import { useAuth } from '@/lib/auth';
import { hasLogo, recalledBrandOrg, usePublicBranding, useFavicon } from '@/lib/branding';

/** Login: única tela com brilho radial de fundo (skill frontend-design → layout.md §Login). */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [rememberedOrg] = useState(() => recalledBrandOrg('internal'));
  const { data: brand, isLoading: brandLoading } = usePublicBranding({
    organization: rememberedOrg,
  });
  useFavicon(brand);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = LoginSchema.safeParse({ email, password });
    if (!parsed.success) {
      const next: typeof errors = {};
      for (const issue of parsed.error.issues) {
        if (issue.path[0] === 'email') next.email = 'Informe um e-mail válido.';
        if (issue.path[0] === 'password') next.password = 'A senha tem ao menos 8 caracteres.';
      }
      setErrors(next);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await login(parsed.data);
      const from = (location.state as { from?: string } | null)?.from;
      void navigate(from && from !== '/login' ? from : '/', { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.code === 'RATE_LIMITED'
            ? 'Muitas tentativas. Aguarde um minuto.'
            : err.message
          : 'Não foi possível entrar. Verifique sua conexão.';
      setErrors({ form: message });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="relative grid min-h-screen place-items-center bg-bg p-4">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(700px 500px at 100% 0%, color-mix(in oklab, var(--accent) 10%, transparent), transparent 70%)',
        }}
      />
      <form
        onSubmit={onSubmit}
        noValidate
        className="glass relative flex w-full max-w-[400px] flex-col gap-5 rounded-lg p-7 shadow-overlay"
      >
        {/* Altura fixa: o logotipo chega depois do primeiro render sem empurrar o formulário. */}
        <div className="flex min-h-[52px] flex-col justify-center">
          {brandLoading ? null : hasLogo(brand) ? (
            <div className="flex flex-col gap-2">
              <BrandLogo branding={brand} size="lg" />
              <h1 className="sr-only">{brand?.display_name}</h1>
              <p className="text-sm text-text-2">Entre para continuar</p>
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              <BrandMonogram name={brand?.display_name ?? 'PM'} className="size-7 text-xs" />
              <div>
                <h1 className="text-lg font-medium leading-tight">
                  {brand?.display_name ?? 'Project Manager'}
                </h1>
                <p className="text-sm text-text-2">Entre para continuar</p>
              </div>
            </div>
          )}
        </div>

        <Field label="E-mail" htmlFor="email" error={errors.email}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            autoFocus
            leading={<Mail />}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            invalid={Boolean(errors.email)}
            placeholder="voce@empresa.com"
          />
        </Field>
        <Field label="Senha" htmlFor="password" error={errors.password}>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            leading={<Lock />}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            invalid={Boolean(errors.password)}
            placeholder="••••••••"
          />
        </Field>

        {errors.form ? (
          <p
            role="alert"
            className="rounded border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {errors.form}
          </p>
        ) : null}

        <Button type="submit" variant="primary" loading={submitting} className="w-full">
          Entrar
        </Button>
      </form>
    </main>
  );
}
